import type { HttpTypes } from '@medusajs/types';

export type SortOptions =
  | 'popular'
  | 'title_asc'
  | 'title_desc'
  | 'price_asc'
  | 'price_desc';

// Shared with FilterBar (`@modules/store/components/product-toolbar`) — the
// PLP's `sortBy` query param values never changed, only the UI rendering them.
export const sortOptions: { value: SortOptions; label: string }[] = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'title_asc', label: 'Name: A to Z' },
  { value: 'title_desc', label: 'Name: Z to A' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
];

type PopularityRow = { units_sold?: number };
type PopularityLink = PopularityRow | PopularityRow[] | null | undefined;

/**
 * Reads a product's units_sold from the read-only product_popularity link.
 * query.graph may surface the link as an object or a single-element array; a
 * missing row means the product has sold nothing → 0. Requires the listing to
 * have been fetched with `+product_popularity.units_sold` in its fields.
 */
export function unitsSoldOf(product: HttpTypes.StoreProduct): number {
  const popularity = (product as { product_popularity?: PopularityLink })
    .product_popularity;
  if (!popularity) return 0;
  const row = Array.isArray(popularity) ? popularity[0] : popularity;
  return typeof row?.units_sold === 'number' ? row.units_sold : 0;
}

function computeMinPrice(product: HttpTypes.StoreProduct): number {
  // Stryker disable next-line ConditionalExpression,EqualityOperator: the
  // `length === 0` check is a fast-path; removing it (mutation → false) leaves
  // the for-loop iterating 0 times so `min` stays Infinity — same return value.
  if (!product.variants || product.variants.length === 0) {
    return Infinity;
  }
  let min = Infinity;
  for (const variant of product.variants) {
    const amount = variant?.calculated_price?.calculated_amount ?? 0;
    // Stryker disable next-line EqualityOperator: `<` → `<=` is equivalent —
    // when amount === min, the reassignment writes the same value, so the
    // tracker still ends on the true minimum.
    if (amount < min) {
      min = amount;
    }
  }
  return min;
}

export function toPriceOrder(sort: SortOptions): 'asc' | 'desc' {
  return sort === 'price_desc' ? 'desc' : 'asc';
}

export function sortProducts(
  products: HttpTypes.StoreProduct[],
  sortBy: SortOptions,
): HttpTypes.StoreProduct[] {
  const sorted = [...products];

  // "Most Popular" ranks by units sold (best sellers first). Products with no
  // popularity row count as 0 and sort last. Only meaningful when the listing
  // was fetched with `+product_popularity.units_sold` — otherwise every product
  // reads as 0 and the stable sort preserves the incoming order.
  if (sortBy === 'popular') {
    sorted.sort((a, b) => unitsSoldOf(b) - unitsSoldOf(a));
    return sorted;
  }

  if (sortBy === 'title_asc') {
    sorted.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
    return sorted;
  }

  if (sortBy === 'title_desc') {
    sorted.sort((a, b) => (b.title ?? '').localeCompare(a.title ?? ''));
    return sorted;
  }

  if (sortBy === 'price_asc' || sortBy === 'price_desc') {
    // Cache per-product min price in a side table rather than writing back
    // onto the source objects — sortProducts must stay pure so the variant
    // metadata it sees doesn't leak into other consumers.
    const minPriceByProduct = new WeakMap<HttpTypes.StoreProduct, number>();
    const minPriceFor = (product: HttpTypes.StoreProduct): number => {
      const cached = minPriceByProduct.get(product);
      // Stryker disable next-line ConditionalExpression,BlockStatement: the cache is
      // a perf optimisation. `computeMinPrice` is deterministic — skipping the
      // cache hit returns the same value (just slower). No observable change.
      if (cached !== undefined) {
        return cached;
      }
      const computed = computeMinPrice(product);
      minPriceByProduct.set(product, computed);
      return computed;
    };

    sorted.sort((a, b) => {
      const diff = minPriceFor(a) - minPriceFor(b);
      return sortBy === 'price_asc' ? diff : -diff;
    });

    return sorted;
  }

  return products;
}
